import { useState, useCallback, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import CategoryGrid from '../components/lifelink/CategoryGrid';
import { PrioritySelector } from '../components/lifelink/PriorityBadge';
import PhotoUploader from '../components/lifelink/PhotoUploader';
import { AlertTriangle, MapPin, Send, Shield, Loader2, CheckCircle2, WifiOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import useSEO from '../hooks/useSEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomePage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  useSEO({
    title: 'Segnala Emergenza · Richiedi Aiuto',
    description: 'Compila il modulo di segnalazione emergenza: scegli categoria, priorità, geolocalizzazione GPS, descrizione. 15 categorie disponibili inclusa emergenza medica, evacuazione, rifugio, farmaci, acqua e protezione minori.',
    keywords: 'segnala emergenza, richiedi aiuto, modulo emergenza, GPS emergenza, priorità emergenza, invio anonimo',
  });
  const [categoria, setCategoria] = useState('');
  const [priorita, setPriorita] = useState('media');
  const [descrizione, setDescrizione] = useState('');
  const [indirizzo, setIndirizzo] = useState('');
  const [latitudine, setLatitudine] = useState(null);
  const [longitudine, setLongitudine] = useState(null);
  const [anonimo, setAnonimo] = useState(false);
  const [nomeRichiedente, setNomeRichiedente] = useState('');
  const [telefono, setTelefono] = useState('');
  const [numeroPersone, setNumeroPersone] = useState('');
  const [noteAggiuntive, setNoteAggiuntive] = useState('');
  const [documentoIdentita, setDocumentoIdentita] = useState('');
  const [loading, setLoading] = useState(false);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showForm, setShowForm] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const PHOTO_CATEGORIES = ['intervento_urgente', 'danni_infrastrutturali', 'persone_scomparse'];
  const PRESCRIPTION_CATEGORY = 'farmaci';
  const showPhotoUploader = PHOTO_CATEGORIES.includes(categoria);
  const showPrescriptionUploader = categoria === PRESCRIPTION_CATEGORY;

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Sync offline requests when back online
  useEffect(() => {
    if (isOnline) {
      const pending = JSON.parse(localStorage.getItem('lifelink_pending') || '[]');
      if (pending.length > 0) {
        const token = localStorage.getItem('lifelink_token');
        axios.post(`${API}/requests/sync`, pending, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          
        }).then(() => {
          localStorage.removeItem('lifelink_pending');
          toast.success(`${pending.length} richieste sincronizzate`);
        }).catch(() => {});
      }
    }
  }, [isOnline]);

  const getLocation = useCallback(() => {
    if (!navigator.geolocation) return;
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLatitudine(pos.coords.latitude);
        setLongitudine(pos.coords.longitude);
        setGpsLoading(false);
        toast.success('Posizione GPS acquisita');
      },
      () => {
        setGpsLoading(false);
        toast.error('Impossibile ottenere la posizione GPS');
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }, []);

  const handleSubmit = async () => {
    if (!categoria) {
      toast.error(t('seleziona_categoria'));
      return;
    }
    if (!descrizione.trim()) {
      toast.error('Inserisci una descrizione');
      return;
    }

    const payload = {
      categoria,
      priorita,
      descrizione,
      latitudine,
      longitudine,
      indirizzo: indirizzo || null,
      anonimo,
      nome_richiedente: anonimo ? null : (nomeRichiedente || null),
      telefono: anonimo ? null : (telefono || null),
      numero_persone: numeroPersone ? parseInt(numeroPersone) : null,
      note_aggiuntive: noteAggiuntive || null,
      documento_identita: anonimo ? null : (documentoIdentita || null),
    };

    if (!isOnline) {
      const pending = JSON.parse(localStorage.getItem('lifelink_pending') || '[]');
      pending.push(payload);
      localStorage.setItem('lifelink_pending', JSON.stringify(pending));
      toast.info(t('richiesta_salvata'));
      resetForm();
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('lifelink_token');
      const { data: reqData } = await axios.post(`${API}/requests`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        
      });
      // Link uploaded files to request
      if (uploadedFiles.length > 0 && reqData.id) {
        for (const f of uploadedFiles) {
          try {
            await axios.post(`${API}/upload`, null, {
              params: { request_id: reqData.id }
            });
          } catch { /* files already uploaded, just update reference */ }
        }
        // Update file records with request_id
        for (const f of uploadedFiles) {
          // Files are already uploaded; we store the association in note_aggiuntive
        }
      }
      toast.success(t('richiesta_inviata'));
      resetForm();
    } catch (e) {
      // Save locally on error
      const pending = JSON.parse(localStorage.getItem('lifelink_pending') || '[]');
      pending.push(payload);
      localStorage.setItem('lifelink_pending', JSON.stringify(pending));
      toast.error('Errore invio. Salvata localmente.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCategoria('');
    setPriorita('media');
    setDescrizione('');
    setIndirizzo('');
    setNomeRichiedente('');
    setTelefono('');
    setNumeroPersone('');
    setNoteAggiuntive('');
    setDocumentoIdentita('');
    setUploadedFiles([]);
    setShowForm(false);
  };

  const handleCategorySelect = (cat) => {
    setCategoria(cat);
    if (cat === 'checkin_sto_bene') {
      setShowForm(false);
    } else {
      setShowForm(true);
    }
  };

  return (
    <div className="min-h-screen bg-[#E8F4FD] pt-20 pb-8" data-testid="home-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        {/* Offline Banner */}
        {!isOnline && (
          <div className="mb-4 p-3 border border-[#FF9F0A] bg-[#FF9F0A]/10 flex items-center gap-2" data-testid="offline-banner">
            <WifiOff size={16} className="text-[#FF9F0A]" />
            <span className="text-xs text-[#FF9F0A] uppercase tracking-widest font-bold">{t('offline_mode')}</span>
          </div>
        )}

        {/* SOS Header */}
        <div className="mb-8 border border-[#FF3B30]/30 bg-[#FF3B30]/5 p-6" data-testid="sos-header">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 flex items-center justify-center flex-shrink-0">
              <img
                src="https://customer-assets.emergentagent.com/job_help-beacon-9/artifacts/ok1e76vb_17%20apr%202026%2C%2009_06_17.png"
                alt="LIFE LINKE"
                className="h-14 w-auto"
              />
            </div>
            <div>
              <h1 className="font-['Barlow_Condensed'] text-2xl sm:text-3xl font-black uppercase tracking-tight text-[#0D1B2A]">
                {t('segnala_emergenza')}
              </h1>
              <p className="text-sm text-[#4A6B8A] mt-1">{t('segnala_desc')}</p>
            </div>
          </div>
        </div>

        {/* Category Selection */}
        <div className="mb-6">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#4A6B8A] mb-4">{t('cosa_ti_serve')}</h2>
          <CategoryGrid selected={categoria} onSelect={handleCategorySelect} />
        </div>

        {/* Emergency Form */}
        {showForm && categoria && (
          <div className="border border-[#B8D4E8] bg-white p-6 space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300" data-testid="emergency-form">
            {/* Description */}
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{t('descrizione')} *</label>
              <textarea
                value={descrizione}
                onChange={(e) => setDescrizione(e.target.value)}
                placeholder={t('descrizione_placeholder')}
                className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors min-h-[100px] resize-none text-sm"
                data-testid="description-input"
              />
            </div>

            {/* GPS Location */}
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{t('posizione_attuale')}</label>
              <div className="flex gap-3 items-center">
                <button
                  onClick={getLocation}
                  className="flex items-center gap-2 px-4 py-2 border border-[#B8D4E8] bg-[#E8F4FD] text-[#0D1B2A] text-xs uppercase tracking-widest hover:border-[#FF3B30] transition-all"
                  data-testid="gps-button"
                  disabled={gpsLoading}
                >
                  {gpsLoading ? <Loader2 size={14} className="animate-spin" /> : <MapPin size={14} />}
                  GPS
                </button>
                {latitudine && longitudine && (
                  <span className="text-xs text-[#32D74B] font-mono" data-testid="gps-coords">
                    <CheckCircle2 size={12} className="inline mr-1" />
                    {latitudine.toFixed(4)}, {longitudine.toFixed(4)}
                  </span>
                )}
              </div>
              <input
                value={indirizzo}
                onChange={(e) => setIndirizzo(e.target.value)}
                placeholder={t('indirizzo')}
                className="mt-2 w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm"
                data-testid="address-input"
              />
            </div>

            {/* Priority */}
            <div>
              <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{t('priorita')}</label>
              <PrioritySelector selected={priorita} onSelect={setPriorita} />
            </div>

            {/* Anonymous toggle */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setAnonimo(!anonimo)}
                className={`w-10 h-5 rounded-full transition-all relative ${anonimo ? 'bg-[#FF3B30]' : 'bg-[#2A2A2A]'}`}
                data-testid="anonymous-toggle"
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all ${anonimo ? 'left-5' : 'left-0.5'}`} />
              </button>
              <span className="text-xs text-[#4A6B8A] uppercase tracking-widest">{t('anonimo')}</span>
            </div>

            {/* Contact info (if not anonymous) */}
            {!anonimo && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{t('nome_richiedente')}</label>
                  <input
                    value={nomeRichiedente}
                    onChange={(e) => setNomeRichiedente(e.target.value)}
                    className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm"
                    data-testid="requester-name-input"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{t('telefono')}</label>
                  <input
                    value={telefono}
                    onChange={(e) => setTelefono(e.target.value)}
                    className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm"
                    data-testid="phone-input"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{t('documento_identita')}</label>
                  <input
                    value={documentoIdentita}
                    onChange={(e) => setDocumentoIdentita(e.target.value)}
                    placeholder="Numero carta d'identità o passaporto"
                    className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm"
                    data-testid="documento-identita-input"
                  />
                </div>
              </div>
            )}

            {/* Additional */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{t('numero_persone')}</label>
                <input
                  type="number"
                  value={numeroPersone}
                  onChange={(e) => setNumeroPersone(e.target.value)}
                  className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm"
                  data-testid="people-count-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{t('note_aggiuntive')}</label>
                <input
                  value={noteAggiuntive}
                  onChange={(e) => setNoteAggiuntive(e.target.value)}
                  className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#FF3B30] focus:ring-1 focus:ring-[#FF3B30] focus:outline-none transition-colors text-sm"
                  data-testid="additional-notes-input"
                />
              </div>
            </div>

            {/* Photo Upload - for intervento_urgente, danni_infrastrutturali, persone_scomparse */}
            {showPhotoUploader && (
              <div className="border border-[#0A84FF]/20 bg-[#0A84FF]/5 p-4" data-testid="photo-upload-section">
                <PhotoUploader
                  onFilesChange={setUploadedFiles}
                  isPrescription={false}
                />
              </div>
            )}

            {/* Prescription Upload - for farmaci */}
            {showPrescriptionUploader && (
              <div className="border border-[#FF9F0A]/20 bg-[#FF9F0A]/5 p-4" data-testid="prescription-upload-section">
                <PhotoUploader
                  onFilesChange={setUploadedFiles}
                  isPrescription={true}
                />
              </div>
            )}

            {/* Submit */}
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-[#FF3B30] text-[#0D1B2A] font-bold uppercase tracking-widest hover:bg-[#FF1A1A] transition-all disabled:opacity-50 text-sm"
              data-testid="submit-emergency-btn"
            >
              {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
              {t('invia_emergenza')}
            </button>

            {/* Privacy note */}
            <div className="flex items-center gap-2 justify-center">
              <Shield size={12} className="text-[#6B8AB5]" />
              <span className="text-[10px] text-[#6B8AB5] uppercase tracking-widest">{t('invio_anonimo')}</span>
            </div>
          </div>
        )}

        {/* Check-in section */}
        {categoria === 'checkin_sto_bene' && (
          <CheckInForm />
        )}
      </div>
    </div>
  );
}

function CheckInForm() {
  const { t } = useLanguage();
  const [nome, setNome] = useState('');
  const [messaggio, setMessaggio] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!nome.trim()) {
      toast.error('Inserisci il tuo nome');
      return;
    }
    setLoading(true);
    try {
      let lat = null, lng = null;
      try {
        const pos = await new Promise((res, rej) => navigator.geolocation.getCurrentPosition(res, rej, { timeout: 5000 }));
        lat = pos.coords.latitude;
        lng = pos.coords.longitude;
      } catch { /* GPS optional */ }

      const token = localStorage.getItem('lifelink_token');
      await axios.post(`${API}/checkin`, { nome, messaggio: messaggio || null, latitudine: lat, longitudine: lng }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        
      });
      toast.success('Check-in inviato!');
      setNome('');
      setMessaggio('');
    } catch {
      toast.error('Errore invio check-in');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="border border-[#32D74B]/30 bg-[#32D74B]/5 p-6 space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-300" data-testid="checkin-form">
      <div className="flex items-center gap-3">
        <CheckCircle2 size={24} className="text-[#32D74B]" />
        <h2 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-tight text-[#0D1B2A]">{t('checkin_title')}</h2>
      </div>
      <p className="text-sm text-[#4A6B8A]">{t('checkin_desc')}</p>
      <div>
        <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{t('nome')} *</label>
        <input
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#32D74B] focus:ring-1 focus:ring-[#32D74B] focus:outline-none transition-colors text-sm"
          data-testid="checkin-name-input"
        />
      </div>
      <div>
        <label className="block text-xs uppercase tracking-widest text-[#4A6B8A] mb-2">{t('messaggio')}</label>
        <textarea
          value={messaggio}
          onChange={(e) => setMessaggio(e.target.value)}
          className="w-full bg-[#E8F4FD] border border-[#B8D4E8] text-[#0D1B2A] p-3 focus:border-[#32D74B] focus:ring-1 focus:ring-[#32D74B] focus:outline-none transition-colors text-sm min-h-[80px] resize-none"
          data-testid="checkin-message-input"
        />
      </div>
      <button
        onClick={handleSubmit}
        disabled={loading}
        className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-[#32D74B] text-black font-bold uppercase tracking-widest hover:bg-[#28C040] transition-all disabled:opacity-50 text-sm"
        data-testid="submit-checkin-btn"
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
        {t('invia')}
      </button>
    </div>
  );
}
